import bgPattern from "@assets/images/bgPattern.svg";
import {
  Box,
  Dialog,
  DialogTitle,
  Stack,
  SxProps,
  Theme,
  useTheme
} from "@mui/material";
import {
  BaseDialogBgImage,
  BaseDialogIconContainer,
  BaseDialogInnerCircle,
  BaseDialogOuterCircle
} from "./BaseDialog.components";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import XButton from "@ui/atoms/XButton/XButton";
import { ReactNode } from "react";
import Icon from "@ui/atoms/Icon/Icon";
import Logo from "@ui/atoms/Logo/Logo";

export interface Props {
  open: boolean;
  title: string;
  subtitle?: string | React.ReactNode;
  onClose?: () => void;
  backdropClose?: boolean;
  intent?: "primary" | "success" | "error" | "warning" | "secondary";
  icon?: ReactNode;
  children?: ReactNode;
  sx?: SxProps<Theme>;
  hasBgImage?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  titleTooltip?: string | ReactNode;
  dataTestId?: string;
}

export const BaseDialog = ({
  backdropClose = true,
  children,
  dataTestId,
  hasBgImage = true,
  icon = <Logo variant="mark" width={24} />,
  intent = "primary",
  maxWidth = "xs",
  onClose,
  open,
  subtitle,
  sx,
  title,
  titleTooltip
}: Props) => {
  const theme = useTheme();
  const isError = intent === "error";
  const isSecondary = intent === "secondary";

  const dialogTitle = (
    <DialogTitle
      sx={{
        fontSize: theme.typography.h6.fontSize,
        fontWeight: theme.typography.fontWeightSemiBold,
        padding: "20px 24px 0"
      }}
    >
      {title}
    </DialogTitle>
  );

  const resolvedTitle = titleTooltip ? (
    <Stack
      direction="row"
      sx={{
        alignItems: "center"
      }}
    >
      {dialogTitle}
      <BaseTooltip title={titleTooltip || ""} placement="right">
        <Stack
          direction="row"
          sx={{
            cursor: "pointer",
            ml: -1.5
          }}
        >
          <Icon name="info" tone="white" />
        </Stack>
      </BaseTooltip>
    </Stack>
  ) : (
    dialogTitle
  );

  const dialogSubtitle = subtitle ? (
    <BaseTypography
      variant="body2"
      weight="regular"
      text={subtitle}
      sx={{
        color: (theme) => theme.vars.palette.text.secondary,
        mt: 1,
        px: 3
      }}
    />
  ) : null;

  const closeButton = !!onClose ? (
    <XButton
      handleClose={onClose}
      dataTestId={dataTestId ? `${dataTestId}-close` : undefined}
      sx={{
        flexShrink: 0,
        mr: -1.8,
        position: "static"
      }}
    />
  ) : null;

  // The dialog Paper is capped at a max-height by MUI and lets its children
  // shrink to fit — but a flex item's implicit min-height is its content size,
  // not 0, so without minHeight: 0 here the Stack refuses to shrink and tall
  // content spills out past the dialog's edge instead of scrolling. Only
  // `children` (the actual body) gets the scroll: title/subtitle and the
  // close button stay fixed, matching how MUI's own DialogContent behaves.
  const scrollableChildren = (
    <Box
      sx={{
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto"
      }}
    >
      {children}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={backdropClose ? onClose : undefined}
      maxWidth={maxWidth}
      fullWidth
      sx={sx}
      data-test-id={dataTestId}
    >
      {hasBgImage ? (
        <>
          <Box sx={{ position: "relative" }}>
            {/* Decorative only — empty alt keeps it out of the accessibility
                tree rather than announcing a filename. */}
            <BaseDialogBgImage src={bgPattern} alt="" aria-hidden />
            <BaseDialogIconContainer>
              <BaseDialogOuterCircle
                color={
                  isError
                    ? `rgba(${theme.vars.palette.error.mainChannel} / 0.08)`
                    : isSecondary
                      ? theme.vars.palette.grey["200"]
                      : `rgba(${theme.vars.palette.primary.lightChannel} / 0.2)`
                }
              >
                <BaseDialogInnerCircle
                  color={
                    isError
                      ? theme.vars.palette.error.main
                      : isSecondary
                        ? theme.vars.palette.grey["500"]
                        : theme.vars.palette.primary.light
                  }
                >
                  {isError ? <Icon name="alert" tone="white" /> : icon}
                </BaseDialogInnerCircle>
              </BaseDialogOuterCircle>
            </BaseDialogIconContainer>

            {closeButton}
          </Box>

          <Stack
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              mt: 10,
              zIndex: 2
            }}
          >
            {resolvedTitle}
            {dialogSubtitle}
            {scrollableChildren}
          </Stack>
        </>
      ) : (
        <Stack
          sx={{
            flex: "1 1 auto",
            minHeight: 0
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between"
            }}
          >
            <Stack>
              {resolvedTitle}
              {dialogSubtitle}
            </Stack>
            {closeButton}
          </Stack>
          {scrollableChildren}
        </Stack>
      )}
    </Dialog>
  );
};
